import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useHandlePaymentVerification from "./useHandlePaymentVerification";
import useGetData from "../../api/useGetData";

/**
 * A page that verifies the the payment, and then informs the user whether their payment was successfull or not.
 * There are few scenarios in this page:
 * - If the payment is a cash payment, then, as there is no action to be made, the success screen is shown.
 * - If the payment is pay by link or credit card payment, then a request is made to the backend to check whether it was successful or not
 * - If it wasn't any kind of payment and the user ended here somehow, they get navigated into the home page
 *
 * How the page detects each type of payment is susceptible to change, but as of the time of writing that, the cash is checked from the browser history state; the credit card is checked from the session storage; and the paybylink is checked from the query params
 *
 * @returns {JSX.Element}
 */

const PaymentStatus = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const isCachePayment = state && state?.paymentMethod === "Cash";
  const isPayByLinkPayment =
    searchParams.get("checkoutId") && searchParams.get("id");
  const savedBookingData = getBookinData();
  const isCardPayment =
  savedBookingData &&
  ["Credit Card", "Mada", "Apple Pay", "APPLEPAY", "Apple pay"].includes(savedBookingData.payment_method);
  const effectRan = useRef(false);
  const [paymentStatus, setPaymentStatus] = useState("loading");
  const { checkCardPayment, checkPayByLink } = useHandlePaymentVerification({
    setPaymentStatus,
  });
  const { getData: getPendingLink } = useGetData({
    route:
      "api/method/airport_transport.api.integrations.paybylink.get_pending_link",
    notLoadData: true,
  });
  useEffect(() => {
    if(paymentStatus === "failed" && !searchParams.get("id")){
      navigate("/", { replace: true });
    }
  }, [paymentStatus])
  

  useEffect(() => {
      if(paymentStatus === "failed"){
        if(localStorage.getItem("preventVisitPaymentStatus") === "true"){
          navigate("/", { replace: true });
        }
      }
  }, [paymentStatus]);

  useEffect(() => {
    if (!isCachePayment && !isPayByLinkPayment && !isCardPayment) {
      navigate("/", { replace: true, state: null });
      return;
    }
    if (effectRan.current) {
      return;
    }
    if (isCachePayment) {
      setPaymentStatus("success");
    } else if (isPayByLinkPayment) {
      checkPayByLink({
        checkoutId: searchParams.get("checkoutId"),
        id: searchParams.get("id"),
      });
    } else if (isCardPayment) {
      checkCardPayment(searchParams.get("id"), savedBookingData);
    } else {
      setPaymentStatus("success");
    }
    return () => (effectRan.current = true);
  }, [state, searchParams]);
  if (paymentStatus === "loading") {
    return (
      <main className="mt-20">
        {/* Form Section */}
        <div className="container mx-auto p-4 mt-5">
          <div className="grid grid-cols-1 gap-3">
            <div className="mx-auto">
              <img
                src="./assets/mybooking/loadingIndicator.png"
                alt="Payment Verification"
                className="size-20"
              />
            </div>
          </div>
          <p className="text-background_steel_blue text-center mt-5 text-lg">
            {t("payment_verification_text")}
          </p>
        </div>
      </main>
    );
  }
  return (
    <main className="mt-20">
      <div className="container mx-auto p-4">
        <div className="grid grid-cols-1 gap-3">
          <div className="mx-auto">
            {paymentStatus === "success" ? (
              <img
                src="./assets/paymentconditions/paymentsuccess.png"
                alt="payment success"
              />
            ) : (
              <img
                src="./assets/paymentconditions/paymentfailed.png"
                alt="payment failed"
              />
            )}
          </div>
        </div>
            <div className="grid grid-cols-1 gap-3">
                <div className="mx-auto">
                  <p className="font-extrabold mt-6 mb-6 font-roboto">
                    {paymentStatus === "success"
                      ? isCachePayment
                        ? t("payment_successful_text_cash")
                        : t("payment_successful_text")
                      : paymentStatus === "booked_before"
                      ? t("payment_booked_before_text")
                      : t("payment_failed_text")}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="mx-auto">
                  <p className="mt-2 font-roboto text-text_lightdark_grey">
                    {paymentStatus === "success"
                      ? isCachePayment
                        ? t("thank_you_big_text_cash")
                        : t("thank_you_big_text")
                      : paymentStatus === "booked_before"
                      ? t("thank_you_booked_before_text")
                      : t("thank_you_payment_failed_text")}
                  </p>
                </div>
              </div>

        {paymentStatus === "success" && (
          <div className="grid md:grid-cols-4 grid-cols-2 gap-3 mt-5">
            <div className="md:col-start-2">
              <button
                type="button"
                onClick={() => navigate("/", { replace: true, state: null })}
                className="bg-white w-full text-black border border-black hover:bg-gray-100 font-medium text-sm px-5 py-2.5 me-2 mb-2"
              >
                {paymentStatus === "success" 
                  ? t("back_home_text")
                  : t("payment_failed_long_text")}
              </button>
            </div>
            <div>
              <button
                type="button"
                onClick={() => navigate("/", { replace: true, state: null })}
                className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium text-sm px-5 py-2.5 me-2 mb-2"
              >
                {t("done_text")}
              </button>
            </div>
          </div>
        )}

        {paymentStatus === "booked_before" && (
            <div className="grid grid-cols-1 gap-3 mt-5 justify-center">
              <div className="w-full max-w-xs mx-auto">
                <button
                  type="button"
                  onClick={() => navigate("/", { replace: true, state: null })}
                  className="bg-white w-full text-black border border-black hover:bg-gray-100 font-medium text-sm px-5 py-2.5 mb-2"
                >
                  {t("back_another_before_home_text")}
                </button>
              </div>
            </div>
          )}

        {paymentStatus === "failed"  && (
          <div className="flex flex-row justify-center items-center mt-5">
            <button
              type="button"
              onClick={async () => {
                if (isPayByLinkPayment) {
                  const data = Object.fromEntries(
                    new URLSearchParams(window.location.search)
                  );
                  Object.keys(data).forEach((key) => {
                    data[key] = decodeURIComponent(data[key]);
                  });
                  const response = await getPendingLink({
                    params: {
                      id: searchParams.get("id"),
                      user: data.user || undefined,
                    },
                    disableUrlParam: true,
                  });
                  if (response.status === 200) {
                    data.qrLink = response.data.data;
                    navigate("/", { state: data });
                  }
                } else if (isCardPayment) {
                  navigate("/", { state:savedBookingData });
                } else {
                  navigate("/", {state: null});
                }
              }}
              class="bg-background_steel_blue text-text_white hover:bg-gray-100 font-medium text-sm px-5 py-2.5 me-2 mb-2 w-fit rounded-md"
            >
              {t("try_again")}
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

const getBookinData = () => {
  try {
    return JSON.parse(sessionStorage.getItem("bookingData"));
  } catch {
    return null;
  }
};
export default PaymentStatus;
